<template>
    <v-row justify="center">
        <!-- Main dialog -->
        <v-dialog v-model="dialog" scrollable persistent max-width="800px">
            <v-card>
                <v-alert v-if="error" tile type="error">{{ error }}</v-alert>
                <v-card-title class="primary white--text">{{
                    formTitle
                }}</v-card-title>
                <v-card-text class="mt-4">
                    <v-form v-model="isFormValid" ref="form">
                        <v-row align="center">
                            <!-- Name -->
                            <v-col cols="12" >
                                <v-text-field
                                    v-model="editedItem.templateName"
                                    :counter="100"
                                    :rules="[
                                        rules.required,
                                        rules.maxLength(50)
                                    ]"
                                    label="Template Name*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>

                        </v-row>

                        <!-- Items divider -->
                        <v-row align="center" class="mx-0 mb-3">
                            <v-divider></v-divider>
                            <span class="pl-3 pr-3">Cost Items</span>
                            <v-divider></v-divider>
                        </v-row>

                        <!-- items -->
                        <v-row
                            v-for="(item, index) in editedItem.items"
                            :key="index"
                            dense
                        >
                            <!-- item name -->
                            <v-col cols="7" md="6">
                                <v-text-field
                                    v-model="item.itemName"
                                    :rules="[
                                        rules.required,
                                        rules.maxLength(50)
                                    ]"
                                    label="Cost Name*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <!-- cost value -->
                            <v-col cols="4" md="5">
                                <v-text-field
                                    v-model="item.costValue"
                                    :rules="[rules.required]"
                                    prefix="€"
                                    type="number"
                                    label="Cost Value*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="1" class="text-right">
                                <v-btn
                                    icon
                                    @click="removeItemFromList(index)"
                                    tabindex="-1"
                                >
                                    <v-icon>close</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-btn
                                    color="primary"
                                    block
                                    outlined
                                    @click="addNewItem()"
                                >
                                    + Add
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="pb-5 pl-8 pr-5">
                    <strong class="mr-2"
                        >Total €{{ calculatedTotalCost.toFixed(2) }}
                    </strong>
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small color="grey" v-on="on"
                                >error_outline</v-icon
                            >
                        </template>
                        <span>Total = SUM(Cost Items) </span>
                    </v-tooltip>
                    <v-spacer></v-spacer>
                    <v-btn text rounded @click="close" tabindex="-1"
                        >Cancel</v-btn
                    >
                    <v-btn
                        outlined
                        rounded
                        :loading="isSaving"
                        :disabled="!isFormValid"
                        color="primary"
                        @click="save"
                        >Save</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";

export default {
    name: "product-fixed-cost-templates-edit-dialog",
    props: ["dialog", "object", "isCopy"],
    components: {},
    data() {
        return {
            isFormValid: false,
            error: null,
            isSaving: false,
            editedItem: {
                id: "",
                templateName: "",
                description: null,
                totalCost: 0,
                items: []
            },
            defaultItem: {
                id: "",
                templateName: "",
                description: null,
                totalCost: 0,
                items: [{ itemName: null, costValue: null }]
            },
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
                // nameRules: [
                //     v => !!v || "Name is required",
                //     v =>
                //         v.length <= 50 || "Name must be less than 50 characters"
                // ]
            }
            // departmentDialog: false,
            // productVariantDialog: false
        };
    },
    computed: {
        formTitle() {
            return this.isCopy
                ? "Copy Template"
                : this.object
                ? "Edit Template"
                : "New Template";
        },
        calculatedTotalCost() {
            let _totalCost = this.editedItem.items.reduce(function(
                accumulator,
                item
            ) {
                return accumulator + parseFloat(item.costValue || 0);
            },
            0);

            return _totalCost;
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    watch: {
        // reset form when dialog open or close
        dialog(val) {
            // if open dialog, initialize selections
            if (val) {
                this.initializeSelections();
            }

            this.resetForm();
        }
    },
    methods: {
        initializeSelections() {},

        resetForm() {
            // reset form state
            this.error = null;
            this.isSaving = false;
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }

            // when editing, set form with object
            if (this.dialog && this.object) {
                this.editedItem.id = this.object.id;
                this.editedItem.templateName = this.object.templateName;
                this.editedItem.items = this.object.productFixedCostTemplateItemSet.edges.map(
                    item => {
                        return {
                            itemName: item.node.itemName,
                            costValue: item.node.costValue
                        };
                    }
                );
            }
            // when new or closing, set form with default
            else {
                this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
            }
        },

        close() {
            this.$emit("update:dialog", false);
            this.$emit("update:object", null);
            this.$emit("update:isCopy", false);
            this.resetForm();
        },

        save() {
            // check if form valid
            if (!this.$refs.form.validate()) {
                return;
            }

            // set form state
            this.error = null;
            this.isSaving = true;

            // if editing
            if (this.object && !this.isCopy) {
                this.$apollo
                    .mutate({
                        // Query
                        mutation: gql`
                            mutation updateProductFixedCostTemplate(
                                $input: UpdateProductFixedCostTemplateInput!
                            ) {
                                updateProductFixedCostTemplate(input: $input) {
                                    productFixedCostTemplate {
                                        id
                                        templateName
                                        totalCost
                                        productFixedCostTemplateItemSet {
                                            edges {
                                                node {
                                                    id
                                                    itemName
                                                    costValue
                                                }
                                            }
                                            totalCount
                                        }
                                    }
                                }
                            }
                        `,
                        // Parameters
                        variables: {
                            input: {
                                id: this.object.id,
                                templateName: this.editedItem.templateName,
                                description: this.editedItem.description,
                                items: this.editedItem.items
                            }
                        }
                    })
                    .then(() => {
                        this.$emit("item-created", false);
                        this.close();

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `Saved!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch(error => {
                        // Error
                        console.error(error);

                        let _error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");

                        // show snackbar
                        const payload = {
                            color: "error",
                            message: _error
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            }
            // if new
            else {
                this.$apollo
                    .mutate({
                        // Query
                        mutation: gql`
                            mutation createProductFixedCostTemplate(
                                $input: CreateProductFixedCostTemplateInput!
                            ) {
                                createProductFixedCostTemplate(input: $input) {
                                    productFixedCostTemplate {
                                        id
                                        templateName
                                        totalCost
                                        productFixedCostTemplateItemSet {
                                            edges {
                                                node {
                                                    id
                                                    itemName
                                                    costValue
                                                }
                                            }
                                            totalCount
                                        }
                                    }
                                }
                            }
                        `,
                        // Parameters
                        variables: {
                            input: {
                                templateName: this.editedItem.templateName,
                                description: this.editedItem.description,
                                items: this.editedItem.items
                            }
                        }
                    })
                    .then(response => {
                        this.$emit(
                            "item-created",
                            response.data.createProductFixedCostTemplate
                        );
                        this.close();

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `Saved!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch(error => {
                        // Error
                        console.error(error);

                        let _error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");

                        // show snackbar
                        const payload = {
                            color: "error",
                            message: _error
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            }
        },

        addNewItem() {
            this.editedItem.items.push({ itemName: "", costValue: "" });
        },

        removeItemFromList(index) {
            if (index > -1) {
                this.editedItem.items.splice(index, 1);
            }
        }
    }
};
</script>